import { createColumnHelper } from "@tanstack/react-table";
import { Eye, Newspaper } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "../../../../core";
import TableActionLink from "@packages/components/table-action-link";
import { format } from "date-fns";
import { tr, enUS } from "date-fns/locale";
import { StudentAssignmentSummaryDto } from "@packages/api/madlenAPI.schemas";

const columnHelper = createColumnHelper<StudentAssignmentSummaryDto>();

export const useAssignmentStudentsTableColumns = (assignmentId: number) => {
  const { t, i18n } = useTranslation();
  const columns = useMemo(
    () => [
      columnHelper.accessor("studentName", {
        header: t("students.table.name"),
        cell: (info) => (
          <div className="flex items-center">
            <Text
              variant="span"
              className="font-medium text-gray-900 dark:text-gray-100"
            >
              {info.getValue()}
            </Text>
          </div>
        ),
      }),
      columnHelper.accessor("studentSurname", {
        header: t("students.table.surname"),
        cell: (info) => (
          <Text
            variant="span"
            className="text-sm text-gray-500 dark:text-gray-400"
          >
            {info.getValue()}
          </Text>
        ),
      }),
      columnHelper.accessor("status", {
        header: t("assignments.table.status.status"),
        cell: (info) => {
          const status = info.getValue();
          const statusConfig = {
            PENDING: {
              bgClass:
                "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
              text: t("assignments.table.status.pending"),
            },
            IN_PROGRESS: {
              bgClass:
                "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
              text: t("assignments.table.status.inProgress"),
            },
            FINISHED: {
              bgClass:
                "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
              text: t("assignments.table.status.completed"),
            },
            DID_NOT_START: {
              bgClass:
                "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
              text: t("assignments.table.status.didNotStart"),
            },
          };

          return (
            <Text
              variant="span"
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${statusConfig[status ?? "PENDING"].bgClass}`}
            >
              {statusConfig[status ?? "PENDING"].text}
            </Text>
          );
        },
      }),
      columnHelper.accessor("startDate", {
        header: t("assignments.table.startDate"),
        cell: (info) => (
          <Text
            variant="span"
            className="text-sm text-gray-500 dark:text-gray-400"
          >
            {info.getValue()
              ? format(new Date(info.getValue() ?? ""), "dd MMMM yyyy, HH:mm", {
                  locale: i18n.language === "tr" ? tr : enUS,
                })
              : "-"}
          </Text>
        ),
      }),
      columnHelper.accessor("endDate", {
        header: t("assignments.table.due"),
        cell: (info) => (
          <Text
            variant="span"
            className="text-sm text-gray-500 dark:text-gray-400"
          >
            {info.getValue()
              ? format(new Date(info.getValue() ?? ""), "dd MMMM yyyy, HH:mm", {
                  locale: i18n.language === "tr" ? tr : enUS,
                })
              : "-"}
          </Text>
        ),
      }),
      columnHelper.accessor("earnedPoints", {
        header: t("assignments.table.earnedPoints"),
        cell: (info) => (
          <Text
            variant="span"
            className="text-sm text-gray-500 dark:text-gray-400"
          >
            {info.getValue() ? info.getValue() : "-"}
          </Text>
        ),
      }),
      columnHelper.accessor("studentId", {
        header: t("students.table.actions.title"),
        cell: (info) => (
          <div className="flex items-center gap-x-4">
            <TableActionLink
              icon={
                <Newspaper
                  className={
                    info.row.original.status === "PENDING"
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }
                  size={24}
                />
              }
              label={
                info.row.original.status === "PENDING"
                  ? t("assignments.table.actions.cannotView")
                  : t("assignments.table.actions.viewStudentAssignment")
              }
              to={`/students/${info.row.original.studentId}/assignments/${assignmentId}`}
              disabled={info.row.original.status === "PENDING"}
            />
            <TableActionLink
              icon={<Eye size={24} />}
              label={t("students.table.actions.viewStudent")}
              to={`/students/${info.getValue()}`}
            />
          </div>
        ),
      }),
    ],
    [t],
  );

  return columns;
};
